import { base64Encode } from "oute-ds-utils";
import {
  ASSET_ID_KEY,
  FOLDER_TYPE,
  PARENT_ID_KEY,
  QUERY_PARAM_KEY,
  WORKSPACE_ID_KEY,
} from "../constants/common-constants";
import { PATH } from "../constants/path-constants";
import {
  BROKEN_LINK_MESSAGE,
  GO_HOME_LABEL,
  resolveThemePalette,
} from "../constants/fallback-ui";
import { buildAssetAppUrl } from "./asset-app-url";

// /open runs before the app mounts so a share link works with or without a session; the
// target app is what authenticates. Destinations come only from the annotation table,
// never from the payload — taking a target from the query string would make this an open
// redirect carrying our own domain.

export const OPEN_REDIRECT_PATH = "/open";

// Routing input from the sender, dropped here rather than forwarded to the app.
const ANNOTATION_KEY = "annotation";
const TYPE_KEY = "type";

// base64url has no "+", which URLSearchParams would decode as a space and atob would
// then strip, failing on the shortened string. Unicode-safe too, unlike a bare btoa.
const decodePayload = (raw: string): Record<string, unknown> | null => {
  try {
    const b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
};

// Anyone can craft a /open URL, so nothing is trusted to be the shape it claims.
const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value : undefined;

// base64Encode because useDecodedParams decodes that way, encodeURIComponent because that
// alphabet includes "+", and `q` because that is the key the route reads.
const viewAssetUrl = (assetId: string, workspaceId?: string): string => {
  const payload = JSON.stringify({
    [ASSET_ID_KEY]: assetId,
    ...(workspaceId ? { [WORKSPACE_ID_KEY]: workspaceId } : {}),
  });
  const encoded = encodeURIComponent(base64Encode(payload));
  return `/${PATH.VIEW_ASSET}?${QUERY_PARAM_KEY}=${encoded}`;
};

export type OpenOutcome =
  | { kind: "none" }
  | { kind: "redirect"; url: string }
  | { kind: "broken" };

export const resolveOpenTarget = (
  pathname: string,
  search: string,
): OpenOutcome => {
  if (pathname.replace(/\/+$/, "") !== OPEN_REDIRECT_PATH) {
    return { kind: "none" };
  }

  const raw = new URLSearchParams(search).get(QUERY_PARAM_KEY);
  // No payload at all is a bare visit to /open, not a broken link.
  if (!raw) return { kind: "redirect", url: "/" };

  const payload = decodePayload(raw);
  const assetId = asString(payload?.[ASSET_ID_KEY]);
  // Mail clients wrap long URLs, so a truncated `q` is the likely cause. Saying so beats
  // a silent bounce to home, which is indistinguishable from a normal visit.
  if (!payload || !assetId) return { kind: "broken" };

  const workspaceId = asString(payload[WORKSPACE_ID_KEY]);
  const appUrl =
    asString(payload[TYPE_KEY]) === FOLDER_TYPE
      ? null
      : buildAssetAppUrl(payload[ANNOTATION_KEY], {
          [WORKSPACE_ID_KEY]: workspaceId,
          [PARENT_ID_KEY]: asString(payload[PARENT_ID_KEY]),
          [ASSET_ID_KEY]: assetId,
        });

  return {
    kind: "redirect",
    url: appUrl || viewAssetUrl(assetId, workspaceId),
  };
};

// Plain DOM: a landing route would sit behind the auth gate and bounce a logged-out
// visitor to login instead of showing them why their link failed. Styles mirror Button's
// default variant and Empty's layout, which cannot be imported into a pre-React path.
const renderBrokenLink = (): void => {
  const root = document.getElementById("root");
  if (!root) return;
  const { bg, fg, muted, primary, primaryFg } = resolveThemePalette();
  // Also on the root element, so the area around the box is not left white in dark mode.
  document.documentElement.style.background = bg;
  root.setAttribute(
    "style",
    `display:flex;min-height:100vh;align-items:center;justify-content:center;` +
      `background:${bg};color:${fg};text-align:center;padding:24px;` +
      `font:14px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif`,
  );
  const message = document.createElement("p");
  message.textContent = BROKEN_LINK_MESSAGE;
  message.setAttribute("style", `margin:0 0 24px;max-width:32rem;color:${muted}`);
  const link = document.createElement("a");
  link.href = "/";
  link.textContent = GO_HOME_LABEL;
  link.setAttribute(
    "style",
    `display:inline-flex;align-items:center;justify-content:center;height:36px;` +
      `padding:0 16px;border-radius:0.625rem;background:${primary};color:${primaryFg};` +
      `font-size:14px;font-weight:500;text-decoration:none`,
  );
  const box = document.createElement("div");
  box.append(message, link);
  root.replaceChildren(box);
};

export const maybeHandleOpenRedirect = (): boolean => {
  if (typeof window === "undefined") return false;

  const outcome = resolveOpenTarget(
    window.location.pathname,
    window.location.search,
  );
  if (outcome.kind === "none") return false;

  if (outcome.kind === "broken") renderBrokenLink();
  // replace(), so Back from the target app does not land here and bounce forward again.
  else window.location.replace(outcome.url);
  return true;
};
